import { Link } from "react-router-dom";

const swatches = [
  { name: "Parchment", hex: "#F4F1EA" },
  { name: "Ink black", hex: "#1A1918" },
  { name: "Wax red", hex: "#8C2A20" },
  { name: "Moss", hex: "#4F634A" },
  { name: "Oak", hex: "#6B4C3A" },
  { name: "Hearth", hex: "#D4A373" },
];

export default function Press() {
  return (
    <main className="max-w-4xl mx-auto px-6 sm:px-10 py-16">
      <p className="label-overline">For journalists, writers, and curious correspondents</p>
      <h1 className="font-display text-5xl sm:text-6xl mt-3">Press kit</h1>
      <div className="fleuron my-8">❦ ❦ ❦</div>

      {/* The pitch */}
      <section className="parchment-card mb-8">
        <p className="label-overline">The one-paragraph pitch</p>
        <p className="font-body text-lg text-ink-iron mt-3 leading-relaxed">
          <em>Hearth &amp; Hermit</em> is a matchmaking platform that revives the 18th-century practice of <strong>ornamental hermits</strong> — solitary souls hired by landowners to live quietly upon their estates, tend gardens, walk grounds, and embody an air of contemplative dignity. Today, with rents impossible and corporations buying up neighbourhoods, we have rebuilt the practice as a modern, dignified alternative to renting. <strong>Hosts</strong> offer a roof and the run of the land. <strong>Hermits</strong> offer their presence — and ask very little. Operating in the USA and UK. Subscriptions are quietly priced; founding members ride free.
        </p>
      </section>

      {/* Quick facts */}
      <section className="parchment-card mb-8" data-testid="press-facts">
        <p className="label-overline">Quick facts</p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-4 font-body text-ink-iron">
          <div><dt className="label-overline">Founded</dt><dd>2026</dd></div>
          <div><dt className="label-overline">Regions</dt><dd>United States · United Kingdom</dd></div>
          <div><dt className="label-overline">Domain</dt><dd>hearthandhermit.com</dd></div>
          <div><dt className="label-overline">Pricing</dt><dd>Free · Starter $1 → $4.99/mo · Standard $4.99/mo · Annual $35.99/yr</dd></div>
          <div><dt className="label-overline">Founding offer</dt><dd>First 200 hosts: free Annual for 12 months. First 100 hermits: free Standard for 6 months.</dd></div>
          <div><dt className="label-overline">Press contact</dt><dd><a className="text-wax-red hover:underline" href="mailto:adam@bigbrainconsulting.ai">adam@bigbrainconsulting.ai</a></dd></div>
        </dl>
      </section>

      {/* Pull quotes */}
      <section className="parchment-card mb-8">
        <p className="label-overline">Quotable lines, for the kindly editor</p>
        <ul className="mt-4 space-y-4 font-body text-lg text-ink-iron">
          <li className="border-l-2 border-wax-red pl-4 italic">"Rent is not always currency."</li>
          <li className="border-l-2 border-wax-red pl-4 italic">"A small middle finger to the housing crisis, dressed in a long cloak."</li>
          <li className="border-l-2 border-wax-red pl-4 italic">"In the 18th century, the wealthy kept ornamental hermits. We are bringing the practice back — with less powdered wig, more dignified housing."</li>
          <li className="border-l-2 border-wax-red pl-4 italic">"Hosts seek a presence. Hermits seek a roof. The contract is the contract."</li>
        </ul>
      </section>

      {/* Founder */}
      <section className="parchment-card mb-8" data-testid="press-founder">
        <p className="label-overline">The founder</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-6 items-start">
          <img
            src="/founder.jpg"
            alt="Adam Thick, founder of Hearth & Hermit"
            className="w-40 h-40 object-cover border border-rule bg-parchment-base"
          />
          <div>
            <h2 className="font-display text-3xl">Adam Thick</h2>
            <p className="font-body italic text-ink-marginalia mt-1">Michigander · BTC miner · AI consultant</p>
            <p className="font-body text-ink-iron mt-4 leading-relaxed">
              Adam Thick is the founder of <em>Hearth &amp; Hermit</em>. He runs <strong>Big Brain Consulting LLC</strong>, where he advises companies on artificial intelligence. He also mines Bitcoin, lives in Michigan, and finds the modern housing market — politely — absurd. <em>Hearth &amp; Hermit</em> is his quiet rebellion: a small return to an older, kinder kind of arrangement.
            </p>
            <p className="font-body text-ink-iron mt-3">
              Reach out: <a className="text-wax-red hover:underline" href="mailto:adam@bigbrainconsulting.ai">adam@bigbrainconsulting.ai</a>
            </p>
          </div>
        </div>
      </section>

      {/* Brand assets */}
      <section className="parchment-card mb-8">
        <p className="label-overline">Brand assets</p>
        <h3 className="font-display text-2xl mt-2 mb-4">Social card (1200 × 630)</h3>
        <a href="/social-card.jpg" target="_blank" rel="noreferrer">
          <img src="/social-card.jpg" alt="Hearth & Hermit social card" className="w-full border border-rule hover:opacity-90 transition-opacity" />
        </a>
        <p className="font-body text-sm text-ink-marginalia italic mt-2">
          Right-click → "Save image as" to download. Free to use in articles about Hearth &amp; Hermit.
        </p>

        <h3 className="font-display text-2xl mt-8 mb-4">Palette</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {swatches.map((s) => (
            <div key={s.hex} className="border border-rule overflow-hidden">
              <div className="h-16" style={{ background: s.hex }} />
              <div className="p-3">
                <p className="font-display text-base">{s.name}</p>
                <p className="font-body text-sm text-ink-marginalia">{s.hex}</p>
              </div>
            </div>
          ))}
        </div>

        <h3 className="font-display text-2xl mt-8 mb-2">Typography</h3>
        <ul className="font-body text-ink-iron space-y-1">
          <li><strong>Display:</strong> Cormorant Garamond (often italic)</li>
          <li><strong>Body:</strong> Lora</li>
          <li><strong>Overlines / small caps:</strong> EB Garamond</li>
        </ul>
      </section>

      {/* The story */}
      <section className="parchment-card mb-8">
        <p className="label-overline">The story, in two paragraphs</p>
        <div className="font-body text-ink-iron leading-relaxed space-y-3 mt-3">
          <p>
            In the 18th century, the very wealthy of England and the colonies engaged in an extraordinary kind of theatre: they hired an ornamental hermit. A real human soul, dressed in a long beard and a brown cloak, paid in modest coin and a roof, to wander the estate and seem profound. The hermits cared for animals. They tended gardens. Some never spoke. Some befriended foxes. The contract was the contract.
          </p>
          <p>
            We do not write to praise the practice in full. We write because something in it deserves remembering: the simple, unbothered exchange of a roof for a presence. Housing as a quiet kindness rather than a quarterly extraction. <em>Hearth &amp; Hermit</em> is, in 2026, an open invitation to revive the courtesy.
          </p>
        </div>
      </section>

      <div className="text-center mt-12">
        <Link to="/" className="ghost-button">Return to the hearth</Link>
      </div>
    </main>
  );
}
